<template>
  <div
    v-if="section"
    :class="{ 'section-disabled': !isSectionEnabled, 'section-readonly': readonly }"
    :data-section="sectionKey"
    class="section-container"
  >
    <div
      :class="{ 'section-completed': allParametersCompleted && section.value === 'checked' }"
      class="section-header px-3 cursor-pointer"
      @click="toggleExpansion"
    >
      <v-icon :color="getSectionIconColor()" class="my-auto mr-0"
              left
              size="32">
        {{ getSectionIcon() }}
      </v-icon>
      <div class="section-info">
        <div>
          <span class="cb-font-size-4  font-weight-bold">{{ sectionIndex + 1 }}. {{ section.name }}</span>
          <span
            v-if="section.value === 'not_required'" class="ml-2">(No aplica)</span>
        </div>
        <div><span class="cb-font-size-5 grey--text">{{ section.description }}</span></div>
      </div>
      <v-spacer />
      <div class="d-flex align-center section-actions">
        <ChecklistStatusToggle
          v-if="!readonly && !allParametersCompleted && allParametersPending"
          :available-values="['pending', 'not_required']"
          :disabled="false"
          :value="section.value"
          @change="updateSectionValue"
        />
        <v-icon
          v-if="section.value == 'checked'"
          color="success"
          size="20"
        >
          check_circle
        </v-icon>
      </div>
    </div>
    <v-expand-transition>
      <div
        v-if="isSectionEnabled && expanded"
        :class="{ 'section-content-disabled': !isSectionEnabled || readonly }"
        class="section-content"
      >
        <div
          v-for="(parameter, paramKey, index) in section.parameters"
          :key="paramKey"
          :data-parameter="paramKey"
          class="parameter-container"
        >
          <ChecklistParameter
            :available-values="availableValues"
            :disabled="!enabled || readonly"
            :index-parameter="index"
            :index-section="sectionIndex"
            :parameter="parameter"
            :process-info="processInfo"
            :readonly="readonly"
            @update-value="$emit('update-parameter-value', paramKey, $event)"
            @update-comment="$emit('update-parameter-comment', paramKey, $event)"
          />
        </div>
      </div>
    </v-expand-transition>
  </div>
</template>
<script>
  import ChecklistParameter from './ChecklistParameter'
  import ChecklistStatusToggle from './ChecklistStatusToggle'
  import checklistService from '@/services/checklistService'

  export default {
    name: 'ChecklistSection',
    components: {
      ChecklistParameter,
      ChecklistStatusToggle
    },
    props: {
      sectionKey: {
        type: String,
        required: true
      },
      section: {
        type: Object,
        required: true
      },
      enabled: {
        type: Boolean,
        default: true
      },
      readonly: {
        type: Boolean,
        default: false
      },
      availableValues: {
        type: Array,
        default: () => ['pending', 'checked', 'not_required']
      },
      processInfo: {
        type: Object,
        default: null
      },
      sectionIndex: {
        type: Number,
        default: 0
      }
    },
    data() {
      return {
        expanded: false // Cambiar a false por defecto
      }
    },
    computed: {
      icon() {
        return checklistService.getSectionIcon(this.section)
      },
      completedCount() {
        if (!this.section || !this.section.parameters) return 0

        return Object.values(this.section.parameters).filter(param => {
          return param.value === 'checked' || param.value === 'not_required'
        }).length
      },
      isSectionEnabled() {
        // Todas las secciones dependen del val
        return this.section.value !== 'not_required'
      },
      allParametersCompleted() {
        return this.completedCount === Object.keys(this.section.parameters).length
      },
      allParametersPending() {
        return Object.values(this.section.parameters).filter(param => {
          return param.value === 'pending'
        }).length == Object.keys(this.section.parameters).length
      },
      sectionHasValueChanges() {
        return checklistService.sectionHasValueChanges(this.section, this.processInfo)
      }
    },
    mounted() {
      // Establecer el estado inicial basado en el valor de la sección
      this.setInitialExpandedState()
    },
    watch: {
      allParametersCompleted(newValue) {
        if (newValue && this.section.value === 'checked') {
          this.expanded = false
        }
      },
      'section.value'(newValue) {
        if (newValue === 'checked' && this.allParametersCompleted) {
          this.expanded = false
        } else if (newValue === 'not_required') {
          this.expanded = false
        } else if (newValue === 'pending') {
          this.expanded = true
        }
      }
    },
    methods: {
      setInitialExpandedState() {
        if (this.section.value === 'checked' && this.allParametersCompleted) {
          this.expanded = false
        } else if (this.section.value === 'not_required') {
          this.expanded = false
        } else if (this.section.value === 'pending' || !this.allParametersCompleted) {
          this.expanded = true
        } else {
          this.expanded = false
        }
      },

      updateSectionValue(newValue) {
        if (this.readonly) return


        this.$emit('update-parameter-value', '_section', newValue)

        if (newValue === 'not_required') {
          this.expanded = false
        }
      },


      toggleExpansion() {

        this.expanded = !this.expanded
      },

      getSectionIcon() {
        if (this.sectionHasValueChanges) {
          return 'warning'
        }
        return this.icon
      },

      getSectionIconColor() {
        return checklistService.getSectionIconColor(
          this.section,
          this.sectionHasValueChanges,
          this.allParametersCompleted
        )
      }
    }
  }
</script>

<style lang="scss">
  @import "@/assets/styles/resources/colors.scss";

  .section-container {
    border-radius: 4px;
    background-color: $color_app_contrast_low;
    border: 1px solid $color_app_contrast_medium;
  }

  .section-header {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 4px;
    border-radius: 4px;
    background-color: $color_app_contrast_low;

  }

  .section-header.cursor-pointer {
    cursor: pointer;
  }

  .section-header.cursor-pointer:hover {
    opacity: 0.8;
  }

  .section-header.section-completed {
    border-left-color: $color_app_success;

  }

  .section-info {
    flex-grow: 1;

    span {
      color: $color_app_text
    }
  }

  .section-content {
    padding: 0 0 0 28px;
    overflow: hidden;
    border-top: 1px solid $color_app_contrast_medium_low;
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;

    :last-child {
      margin-bottom: 0;
    }
  }

  .section-actions {
    align-self: stretch;
  }

  .section-disabled {
    opacity: 0.7;
  }

  .section-readonly {
    opacity: 0.8;
  }

  .parameter-highlight-background {
    .parameter-info {
      background-color: $color_app_primary_focus !important;
    }
  }


</style>